import React, { useMemo, useCallback } from "react";
import { Box, VStack } from "@chakra-ui/react";
import { Building2 } from "lucide-react";
import { 
  useGetOrganizationsQuery,
  useOrganizationFilters,
  useOrganizationActions,
  createOrganizationColumns,
  createOrganizationActions,
  ORGANIZATION_STATUS_OPTIONS,
} from "@entities/organizations";
import { OrganizationCreateModal, OrganizationEditModal } from "@entities/organizations/components/OrganizationCreateEditModal";
import { OrganizationDetailsHashModal } from "@entities/organizations/components/OrganizationDetailsModal";
import EditConfigurationModal from "@entities/organizations/components/EditConfigurationModal";
import { useGetPaymentProvidersQuery } from "@entities/payment-providers/api";
import EntityTableSection from "@/components/section/EntityTableSection";
import PageHeader from "@/components/ui/PageHeader";
import SearchAndFiltersSection from "@/components/section/SearchAndFiltersSection";
import ErrorAlert from "@/components/ErrorAlert";
import LoadingSkeleton from "@/components/LoadingSkeleton";
import ConfirmationModal from "@/components/modals/ConfirmationModal";
import Pagination from "@/components/ui/Pagination";

const OrganizationsPage: React.FC = () => {
  // Performance optimized with memoized hooks and callbacks
  const { filters, queryParams, handleFilterChange } = useOrganizationFilters();
  const actions = useOrganizationActions();

  const {
    data: organizationsResponse,
    isLoading,
    isError,
    error,
    isFetching,
    refetch,
  } = useGetOrganizationsQuery(queryParams, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  const {
    data: paymentProviders,
    isLoading: isLoadingPaymentProviders,
  } = useGetPaymentProvidersQuery();

  // Extract organizations array from paginated response
  const organizationsArray = organizationsResponse?.data || [];
  const totalOrganizations = organizationsResponse?.total || 0;

  // Get payment provider options from API
  const paymentProviderOptions = useMemo(() => {
    if (!paymentProviders) {
      return [
        { value: "all", label: "All Providers" },
        { value: "none", label: "No Provider" },
      ];
    }

    const sortedProviders = [...paymentProviders]
      .sort((a, b) => a.display_name.localeCompare(b.display_name));
    
    return [
      { value: "all", label: "All Providers" },
      { value: "none", label: "No Provider" },
      ...sortedProviders.map(provider => ({ 
        value: provider.name, 
        label: provider.display_name 
      }))
    ];
  }, [paymentProviders]);

  // Memoize search filters configuration
  const searchFilters = useMemo(
    () => [
      {
        key: "status",
        label: "All Statuses",
        value: filters.status,
        options: [
          { value: "all", label: "All Statuses" },
          ...ORGANIZATION_STATUS_OPTIONS,
        ],
        onChange: (value: string) => handleFilterChange("status", value),
      },
      {
        key: "paymentProvider",
        label: "All Providers",
        value: filters.paymentProvider,
        options: paymentProviderOptions,
        onChange: (value: string) => handleFilterChange("paymentProvider", value),
      },
    ],
    [filters.status, filters.paymentProvider, handleFilterChange, paymentProviderOptions]
  );

  // Memoize table columns to prevent recreation
  const tableColumns = useMemo(() => createOrganizationColumns(), []);

  // Memoize table actions
  const tableActions = useMemo(
    () => createOrganizationActions(actions.handleAction, actions.isDeleting),
    [actions.handleAction, actions.isDeleting]
  );

  // Memoize delete confirmation handler
  const handleDeleteConfirm = useCallback(async () => {
    const success = await actions.handleDelete();
    if (success) {
      refetch();
    }
  }, [actions.handleDelete, refetch]);

  // Memoize search change handler to prevent inline function re-renders
  const handleSearchChange = useCallback(
    (value: string) => {
      handleFilterChange("search", value);
    },
    [handleFilterChange]
  );

  // Memoize modal success handler to refresh data after create/update
  const handleModalSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  // Memoize page change handler
  const handlePageChange = useCallback((page: number) => {
    handleFilterChange('page', page);
  }, [handleFilterChange]);

  // Memoize page size change handler
  const handlePageSizeChange = useCallback((pageSize: number) => {
    handleFilterChange('limit', pageSize);
  }, [handleFilterChange]);

  if (isLoading && !organizationsArray.length) {
    return <LoadingSkeleton rows={5} />;
  }

  if (isError) {
    return (
      <ErrorAlert
        isError={true}
        title="Failed to load organizations"
        message={error?.toString() || "An error occurred while loading data"}
        onRetry={refetch}
      />
    );
  }

  return (
    <Box maxW="full" mx="auto" pb={8}>
      <VStack gap={6} align="stretch">
        <PageHeader
          title="Organizations Management"
          subtitle={`${totalOrganizations} total organizations`}
          onAddNew={actions.handleCreateNew}
          addButtonText="Create Organization"
          onRefresh={refetch}
          isRefreshing={isFetching}
        />

        <SearchAndFiltersSection
          searchValue={filters.search}
          searchPlaceholder="Search by organization name..."
          onSearchChange={handleSearchChange}
          filters={searchFilters}
        />

        <EntityTableSection
          data={organizationsArray}
          columns={tableColumns}
          actions={tableActions}
          isLoading={isFetching}
          onRowClick={actions.handleRowClick}
          emptyState={{
            icon: Building2,
            title: "No organizations found",
            description: "Create your first organization to get started",
          }}
        />

        {/* Pagination */}
        {organizationsResponse && (
          <Pagination
            paginationInfo={{
              current_page: organizationsResponse.current_page,
              last_page: organizationsResponse.last_page,
              per_page: organizationsResponse.per_page,
              total: organizationsResponse.total,
            }}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            showPageSizeSelector={true}
            isLoading={isFetching}
          />
        )}

        {/* Hash-managed Modals - Each modal self-determines if it should render */}
        <OrganizationDetailsHashModal />

        <OrganizationCreateModal
          onSuccess={handleModalSuccess}
        />

        <OrganizationEditModal
          onSuccess={handleModalSuccess}
        />

        <EditConfigurationModal
          {...actions.editConfigurationModalProps}
          organization={actions.selectedOrganizationForConfiguration}
        />

        {/* Traditional modal for delete confirmation */}
        {actions.deleteConfirmModalProps.isOpen && (
          <ConfirmationModal
            {...actions.deleteConfirmModalProps}
            onConfirm={handleDeleteConfirm}
          />
        )}
      </VStack>
    </Box>
  );
};

export default OrganizationsPage; 